import { computed } from '@angular/core';
import type { FieldTree } from '@angular/forms/signals';
import { validateStandardSchema } from '@angular/forms/signals';

import type {
    FormWidgetElement,
    LegacyLoginEduDefault,
    LegacyLoginEduZero,
    LoginEduAuthorizationButtonLegacy,
    LoginEduButtonLegacy,
    LoginEduCheckboxLegacy,
    LoginEduFormTriggerLegacy,
    LoginEduRegistrationButtonLegacy,
    LoginEduWidgetEntity,
} from '@sites/entity-core';
import { loginEduLegacyDefaultV0Schema, loginEduLegacyZeroV0Schema } from '@sites/entity-core';

import type { BaseLegacyEntityForm, BaseLegacyEntityFormOptions } from '../../core/create-base-legacy-entity-form';
import { configureCommonReadonly, createBaseLegacyEntityForm } from '../../core/create-base-legacy-entity-form';
import { arrayMove } from '../../core/array-move';
import { composeForm } from '../../core/compose-form';
import { extendedPositionPlugin, widgetPositionPlugin } from '../../plugins/widget/position.plugin';
import { visibilityPlugin } from '../../plugins/widget/visibility.plugin';
import { legacyWidgetEnvelopePlugin } from '../../plugins/widget/legacy-widget-envelope.plugin';

export type LoginEduFormModel = LoginEduWidgetEntity;
export type LoginEduDataModel = LoginEduWidgetEntity['data'];

/**
 * Login-edu accessors shared verbatim by both variant assemblers — the course
 * binding, the button/checkbox/element arrays (with their immutable array
 * sugar), the styling groups and the view-mode flags. These are structurally
 * identical on both variants (only `position` differs, and that comes from the
 * position plugins), so the lazy `raw` thunk is narrowed to the default
 * variant's FieldTree internally — the form/payment-form pattern.
 *
 * Exposed via getters so the FieldTree paths materialise lazily (after
 * `syncFromEntity` populates the model) — composed with {@link composeForm},
 * never object spread.
 *
 * **Beware optional branches**: `analyticsSettings`, `courseTariffId` and
 * `courseTariffDetails` may be absent from legacy persisted data — FieldTree
 * children only materialise for keys present in the model, so those writes go
 * **through the parent data node** (the missing-key trap) and reads should
 * prefer `value()`.
 */
function loginEduSharedAccessors(raw: () => FieldTree<LegacyLoginEduDefault> | FieldTree<LegacyLoginEduZero>) {
    const r = raw as () => FieldTree<LegacyLoginEduDefault>;

    // --- Derived reads -----------------------------------------------------
    //
    // The two auth entries are looked up by their `buttonType` tag rather than
    // by index (the user can reorder the row), and several view-level questions
    // — which trigger is active, what label to render — are pure functions of
    // them. They are `computed` so every consumer shares one memoised result
    // instead of re-scanning the array on each change-detection pass.
    //
    // The thunk stays lazy: these bodies only run once something reads them,
    // i.e. after `syncFromEntity` has populated the model.

    const buttons = computed(() => r().data.buttons().value());

    const registrationButton = computed(
        () =>
            buttons().find((button) => button.buttonType === 'registration') as
                | LoginEduRegistrationButtonLegacy
                | undefined
    );

    const authorizationButton = computed(
        () =>
            buttons().find((button) => button.buttonType === 'authorization') as
                | LoginEduAuthorizationButtonLegacy
                | undefined
    );

    const registrationTrigger = computed<LoginEduFormTriggerLegacy | undefined>(
        () => registrationButton()?.modalTrigger
    );
    const authorizationTrigger = computed<LoginEduFormTriggerLegacy | undefined>(
        () => authorizationButton()?.modalTrigger
    );

    const isModalForm = computed(() => r().data.formView().value() === 'modal');

    return {
        // --- Derived reads (value signals, not FieldTree nodes) ---
        /** The `registration` entry of `buttons`, found by tag. */
        registrationButton,
        /** The `authorization` entry of `buttons`, found by tag. */
        authorizationButton,
        registrationTrigger,
        authorizationTrigger,

        /** `formView === 'modal'` — the auth forms open in a modal. */
        isModalForm,
        /** `formView === 'static'` — the auth form is embedded in the page. */
        isStaticForm: computed(() => r().data.formView().value() === 'static'),

        /**
         * Whether the *other* form's link should be offered under the rendered
         * one — driven by whether that form's button is enabled at all.
         */
        isShowRegistrationFormLink: computed(() => registrationButton()?.isActive ?? false),
        isShowAuthorizationFormLink: computed(() => authorizationButton()?.isActive ?? false),

        /**
         * Labels of the two auth buttons. In modal mode the trigger owns the
         * label; embedded, it comes from the button itself (and for the
         * registration group, from its inner `registrationButton`).
         */
        registrationButtonLabel: computed(
            () => (isModalForm() ? registrationTrigger()?.label : registrationButton()?.registrationButton?.label) ?? ''
        ),
        authorizationButtonLabel: computed(
            () => (isModalForm() ? authorizationTrigger()?.label : authorizationButton()?.label) ?? ''
        ),

        /** Whether each trigger renders — the trigger's own flag in modal mode. */
        isRegistrationTriggerActive: computed(() =>
            isModalForm() && registrationTrigger()
                ? (registrationTrigger()!.isActive ?? false)
                : (registrationButton()?.isActive ?? false)
        ),
        isAuthorizationTriggerActive: computed(() =>
            isModalForm() && authorizationTrigger()
                ? (authorizationTrigger()!.isActive ?? false)
                : (authorizationButton()?.isActive ?? false)
        ),

        // --- Course binding ---
        get course() {
            return r().data.course;
        },
        setCourse: (value: LoginEduDataModel['course']) => {
            r().data.course().value.set(value);
        },
        /** Optional branch — may be absent on legacy data; prefer `value()` reads. */
        get courseTariffId() {
            return r().data.courseTariffId;
        },
        setCourseTariffId: (value: LoginEduDataModel['courseTariffId']) => {
            // Whole-data write: the key is optional, so its FieldTree child may
            // not exist yet (the missing-key trap) — writing through the parent
            // both sets the value and materialises the child.
            r()
                .data()
                .value.update((data) => ({ ...data, courseTariffId: value }));
        },
        /** Optional branch — may be absent on legacy data; prefer `value()` reads. */
        get courseTariffDetails() {
            return r().data.courseTariffDetails;
        },
        setCourseTariffDetails: (value: LoginEduDataModel['courseTariffDetails']) => {
            r()
                .data()
                .value.update((data) => ({ ...data, courseTariffDetails: value }));
        },

        // --- Buttons (array of a `buttonType` discriminated union) ---
        get buttons() {
            return r().data.buttons;
        },
        setButtons: (value: LoginEduButtonLegacy[]) => {
            r().data.buttons().value.set(value);
        },
        buttonAt: (index: number): FieldTree<LoginEduButtonLegacy> | undefined => {
            return (r().data.buttons as unknown as Record<number, FieldTree<LoginEduButtonLegacy>>)[index];
        },
        addButton: (item: LoginEduButtonLegacy) => {
            r()
                .data.buttons()
                .value.update((arr) => [...arr, item]);
        },
        removeButton: (index: number) => {
            r()
                .data.buttons()
                .value.update((arr) => arr.filter((_, i) => i !== index));
        },
        moveButton: (from: number, to: number) => {
            r()
                .data.buttons()
                .value.update((arr) => arrayMove(arr, from, to));
        },
        /**
         * Patch one button in place. The array holds a discriminated union, so
         * the merged result is re-asserted: callers are expected to patch a
         * button with fields belonging to its own variant.
         */
        updateButton: (index: number, patch: Partial<LoginEduButtonLegacy>) => {
            r()
                .data.buttons()
                .value.update((arr) =>
                    arr.map((b, i) => (i === index ? ({ ...b, ...patch } as LoginEduButtonLegacy) : b))
                );
        },

        // --- Consent checkboxes (array) ---
        get checkboxes() {
            return r().data.checkboxes;
        },
        setCheckboxes: (value: LoginEduCheckboxLegacy[]) => {
            r().data.checkboxes().value.set(value);
        },
        checkboxAt: (index: number): FieldTree<LoginEduCheckboxLegacy> | undefined => {
            return (r().data.checkboxes as unknown as Record<number, FieldTree<LoginEduCheckboxLegacy>>)[index];
        },
        addCheckbox: (item: LoginEduCheckboxLegacy) => {
            r()
                .data.checkboxes()
                .value.update((arr) => [...arr, item]);
        },
        removeCheckbox: (index: number) => {
            r()
                .data.checkboxes()
                .value.update((arr) => arr.filter((_, i) => i !== index));
        },
        moveCheckbox: (from: number, to: number) => {
            r()
                .data.checkboxes()
                .value.update((arr) => arrayMove(arr, from, to));
        },
        updateCheckbox: (index: number, patch: Partial<LoginEduCheckboxLegacy>) => {
            r()
                .data.checkboxes()
                .value.update((arr) => arr.map((c, i) => (i === index ? { ...c, ...patch } : c)));
        },
        get registrationCheckbox() {
            return r().data.registrationCheckbox;
        },
        setRegistrationCheckbox: (value: LoginEduDataModel['registrationCheckbox']) => {
            r().data.registrationCheckbox().value.set(value);
        },

        // --- Sign-up form elements (array) ---
        get signUpElements() {
            return r().data.signUpElements;
        },
        setSignUpElements: (value: FormWidgetElement[]) => {
            r().data.signUpElements().value.set(value);
        },
        signUpElementAt: (index: number): FieldTree<FormWidgetElement> | undefined => {
            return (r().data.signUpElements as unknown as Record<number, FieldTree<FormWidgetElement>>)[index];
        },
        addSignUpElement: (item: FormWidgetElement) => {
            r()
                .data.signUpElements()
                .value.update((arr) => [...arr, item]);
        },
        removeSignUpElement: (index: number) => {
            r()
                .data.signUpElements()
                .value.update((arr) => arr.filter((_, i) => i !== index));
        },
        moveSignUpElement: (from: number, to: number) => {
            r()
                .data.signUpElements()
                .value.update((arr) => arrayMove(arr, from, to));
        },
        updateSignUpElement: (index: number, patch: Partial<FormWidgetElement>) => {
            r()
                .data.signUpElements()
                .value.update((arr) => arr.map((e, i) => (i === index ? { ...e, ...patch } : e)));
        },

        // --- Sign-in form elements (array) ---
        get signInElements() {
            return r().data.signInElements;
        },
        setSignInElements: (value: FormWidgetElement[]) => {
            r().data.signInElements().value.set(value);
        },
        signInElementAt: (index: number): FieldTree<FormWidgetElement> | undefined => {
            return (r().data.signInElements as unknown as Record<number, FieldTree<FormWidgetElement>>)[index];
        },
        addSignInElement: (item: FormWidgetElement) => {
            r()
                .data.signInElements()
                .value.update((arr) => [...arr, item]);
        },
        removeSignInElement: (index: number) => {
            r()
                .data.signInElements()
                .value.update((arr) => arr.filter((_, i) => i !== index));
        },
        moveSignInElement: (from: number, to: number) => {
            r()
                .data.signInElements()
                .value.update((arr) => arrayMove(arr, from, to));
        },
        updateSignInElement: (index: number, patch: Partial<FormWidgetElement>) => {
            r()
                .data.signInElements()
                .value.update((arr) => arr.map((e, i) => (i === index ? { ...e, ...patch } : e)));
        },

        // --- Styling groups (FieldTree branch + whole-value setter) ---
        get inputSettings() {
            return r().data.inputSettings;
        },
        setInputSettings: (value: LoginEduDataModel['inputSettings']) => {
            r().data.inputSettings().value.set(value);
        },
        get additionalFormSettings() {
            return r().data.additionalFormSettings;
        },
        setAdditionalFormSettings: (value: LoginEduDataModel['additionalFormSettings']) => {
            r().data.additionalFormSettings().value.set(value);
        },
        /** Optional branch — may be absent on legacy data; prefer `value()` reads. */
        get analyticsSettings() {
            return r().data.analyticsSettings;
        },
        setAnalyticsSettings: (value: LoginEduDataModel['analyticsSettings']) => {
            r()
                .data()
                .value.update((data) => ({ ...data, analyticsSettings: value }));
        },

        // --- View mode ---
        get horizontalPosition() {
            return r().data.horizontalPosition;
        },
        setHorizontalPosition: (value: LoginEduDataModel['horizontalPosition']) => {
            r().data.horizontalPosition().value.set(value);
        },
        get formView() {
            return r().data.formView;
        },
        setFormView: (value: LoginEduDataModel['formView']) => {
            r().data.formView().value.set(value);
        },
        get defaultForm() {
            return r().data.defaultForm;
        },
        setDefaultForm: (value: LoginEduDataModel['defaultForm']) => {
            r().data.defaultForm().value.set(value);
        },
        get linkBetweenForms() {
            return r().data.linkBetweenForms;
        },
        setLinkBetweenForms: (value: LoginEduDataModel['linkBetweenForms']) => {
            r().data.linkBetweenForms().value.set(value);
        },
    };
}

/**
 * Zero-only accessors. `isSynchronizeStyle` — the per-device style-sync toggle
 * the zero host renders — exists on the zero variant alone (the default
 * placement has no per-device style split), so it is installed here rather than
 * in the shared accessors.
 */
function loginEduZeroAccessors(raw: () => FieldTree<LegacyLoginEduZero>) {
    return {
        /** Optional branch — may be absent on legacy data; prefer `value()` reads. */
        get isSynchronizeStyle() {
            return raw().data.isSynchronizeStyle;
        },
        setIsSynchronizeStyle: (value: boolean) => {
            raw()
                .data()
                .value.update((data) => ({ ...data, isSynchronizeStyle: value }));
        },
    };
}

/**
 * Per-variant accessor assemblers. Exactly like form/payment-form, the two
 * login-edu variants share every accessor and differ **only** in the `position`
 * branch type: the default form's grid is the simple per-device
 * `WidgetPosition` (via {@link widgetPositionPlugin}), the zero form's is the
 * full `ExtendedPosition` (anchors, heights, changed-flags — via
 * {@link extendedPositionPlugin}), plus the zero-only style-sync toggle.
 */
function assembleDefaultLoginEdu(base: BaseLegacyEntityForm<LegacyLoginEduDefault>) {
    const _raw = base._raw;

    // Checked widening (no cast): the variant form is assignable to the
    // entity-union form, so the dispatcher's union return collapses into a
    // single useful shape for union-only callers.
    const widenedBase: BaseLegacyEntityForm<LoginEduWidgetEntity> = base;

    return composeForm(
        widenedBase,
        legacyWidgetEnvelopePlugin(() => _raw),
        loginEduSharedAccessors(() => _raw),
        visibilityPlugin(() => _raw.data),
        // Default-only layout — the simple per-device position grid.
        widgetPositionPlugin(() => _raw.data)
    );
}

function assembleZeroLoginEdu(base: BaseLegacyEntityForm<LegacyLoginEduZero>) {
    const _raw = base._raw;

    const widenedBase: BaseLegacyEntityForm<LoginEduWidgetEntity> = base;

    return composeForm(
        widenedBase,
        legacyWidgetEnvelopePlugin(() => _raw),
        loginEduSharedAccessors(() => _raw),
        loginEduZeroAccessors(() => _raw),
        visibilityPlugin(() => _raw.data),
        // Zero-only layout — the full extended-position grid.
        extendedPositionPlugin(() => _raw.data)
    );
}

/**
 * Signal-form view of a **default-placement** login-edu widget. Validates
 * against the legacy default envelope schema, which permits an absent `type`
 * field for parity with pre-discriminator persisted data on disk.
 */
export function createLegacyLoginEduDefaultForm(options?: BaseLegacyEntityFormOptions<LegacyLoginEduDefault>) {
    const base = createBaseLegacyEntityForm<LegacyLoginEduDefault>((schemaPath) => {
        validateStandardSchema(schemaPath, loginEduLegacyDefaultV0Schema);
        configureCommonReadonly(schemaPath);
    }, options);

    return assembleDefaultLoginEdu(base);
}

/**
 * Signal-form view of a **zero-block-placement** login-edu widget. Validates
 * against the legacy zero envelope schema, which requires `type: 'zero'` on the
 * envelope (the host-app factory copies `meta.type` onto the entity at creation
 * time). Diverges from {@link createLegacyLoginEduDefaultForm} only on
 * `position` and the zero-only `isSynchronizeStyle` toggle.
 */
export function createLegacyLoginEduZeroForm(options?: BaseLegacyEntityFormOptions<LegacyLoginEduZero>) {
    const base = createBaseLegacyEntityForm<LegacyLoginEduZero>((schemaPath) => {
        validateStandardSchema(schemaPath, loginEduLegacyZeroV0Schema);
        configureCommonReadonly(schemaPath);
    }, options);

    return assembleZeroLoginEdu(base);
}

/**
 * Variant dispatcher. Callers that only know `LoginEduWidgetEntity` (the union
 * of default + zero) use this — `WebLegacyWidgetSignalFormService` for instance — and
 * the dispatcher reads the variant tag from `options.initialData?.type` to pick
 * the matching per-variant factory.
 */
export function createLoginEduForm(options?: BaseLegacyEntityFormOptions<LoginEduWidgetEntity>) {
    const variant = options?.initialData?.type;

    if (variant === 'zero') {
        return createLegacyLoginEduZeroForm(options as BaseLegacyEntityFormOptions<LegacyLoginEduZero>);
    }

    return createLegacyLoginEduDefaultForm(options as BaseLegacyEntityFormOptions<LegacyLoginEduDefault>);
}

export type LoginEduForm = ReturnType<typeof createLoginEduForm>;
export type LoginEduDefaultForm = ReturnType<typeof createLegacyLoginEduDefaultForm>;
export type LoginEduZeroForm = ReturnType<typeof createLegacyLoginEduZeroForm>;
